import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PORTAL_PATHS } from '@x9/billing';
import { useAuthUser } from './hooks/useAuthUser';
import { useUserRecovery } from './hooks/useUserRecovery';
import { navigateTo } from '@x9/web-work/navigation';
import {
  type AppRoute,
  isPortalPath,
  pathForRoute,
  pathToRoute,
  replacePath,
  requiresAuth,
  isAdmittedPhoneSession,
  setPath,
} from './routing';
import CheckoutPage from './pages/CheckoutPage';
import PackUpgradeCheckoutPage from './pages/PackUpgradeCheckoutPage';
import PendingPage from './pages/PendingPage';
import AccountPage from './pages/AccountPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SeatsPage from './pages/SeatsPage';
import PortalLoader from './components/PortalLoader';
import { marketingPricingUrl, openMarketingHome, openMarketingPricing } from './lib/marketing';

const AuthExperience = lazy(() =>
  import('./components/signin/AuthExperience').then((m) => ({ default: m.AuthExperience })),
);
const EmailGateForm = lazy(() =>
  import('./components/signin/EmailGateForm').then((m) => ({ default: m.EmailGateForm })),
);
const SignInForm = lazy(() =>
  import('./components/signin/SignInForm').then((m) => ({ default: m.SignInForm })),
);
const TermsGateForm = lazy(() =>
  import('./components/signin/TermsGateForm').then((m) => ({ default: m.TermsGateForm })),
);
const StartPastaPage = lazy(() => import('./pages/start/StartPastaPage'));
const StartRoutinePage = lazy(() => import('./pages/start/StartRoutinePage'));

/**
 * Every signed-in portal area requires terms consent + a verified account
 * email before we treat the session as an operable X9 account.
 */
function needsOnboarding(route: AppRoute | null): boolean {
  if (!route) return false;
  return (
    requiresAuth(route)
    || route.name === 'signin'
    || route.name === 'email'
    || route.name === 'verify-email'
  );
}

function continuePathFrom(route: AppRoute | null): string {
  if (!route) return PORTAL_PATHS.HOME;
  const raw =
    route.name === 'email' || route.name === 'signin'
      ? route.nextPath
      : pathForRoute(route);
  if (!raw.startsWith('/')) return PORTAL_PATHS.HOME;
  if (
    raw.startsWith(PORTAL_PATHS.SIGNIN)
    || raw.startsWith(PORTAL_PATHS.EMAIL)
    || raw.startsWith(PORTAL_PATHS.PRICING)
    || raw.startsWith(PORTAL_PATHS.VERIFY_EMAIL)
  ) {
    return PORTAL_PATHS.HOME;
  }
  return raw;
}

type AppProps = {
  /**
   * When the work tree is gated on desktop, `/` is not a portal path.
   * Seed the SMS / terms / email shell so we never render a blank route.
   */
  forcedRoute?: AppRoute;
};

export default function App({ forcedRoute }: AppProps = {}) {
  const { user, loading, handoffError } = useAuthUser();
  const { recovery, recoveryReady } = useUserRecovery(!!user);
  const initial = useMemo(() => pathToRoute() ?? forcedRoute ?? null, [forcedRoute]);
  const [route, setRoute] = useState<AppRoute | null>(initial);

  useEffect(() => {
    const onPop = () => setRoute(pathToRoute() ?? forcedRoute ?? null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [forcedRoute]);

  // Canonicalise legacy aliases → Conta. Início (`/usage`) is Conta → Uso.
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (route?.name === 'usage' && path === PORTAL_PATHS.USAGE) {
      replacePath(`${PORTAL_PATHS.ACCOUNT}?tab=uso`);
    }
    if (route?.name === 'account') {
      if (
        path === PORTAL_PATHS.BILLING
        || path === PORTAL_PATHS.PLAN
        || path === PORTAL_PATHS.PAYMENT
        || path === PORTAL_PATHS.HISTORY
      ) {
        replacePath(PORTAL_PATHS.ACCOUNT);
      }
    }
  }, [route]);

  const go = useCallback((next: AppRoute) => {
    setPath(pathForRoute(next));
    setRoute(next);
  }, []);

  const goPath = useCallback((path: string) => {
    const url = new URL(path, window.location.origin);
    if (!isPortalPath(url.pathname)) {
      navigateTo(`${url.pathname}${url.search}`);
      return;
    }
    const next = pathToRoute(url.pathname, url.search);
    if (!next) {
      navigateTo(`${url.pathname}${url.search}`);
      return;
    }
    setPath(`${url.pathname}${url.search}`);
    setRoute(next);
  }, []);

  const emailVerified = recovery.recoveryEmailVerified && !!recovery.recoveryEmail;
  const termsAccepted = recovery.termsAccepted;

  const continueDest = continuePathFrom(route);
  let continueDestIsPortal = false;
  try {
    continueDestIsPortal = isPortalPath(new URL(continueDest, window.location.origin).pathname);
  } catch {
    continueDestIsPortal = false;
  }

  // Capture whether this tab restored an existing session (vs SMS just now).
  // Restored sessions skip the Three.js sign-in chrome; new sign-up keeps it
  // mounted across SMS → terms → email.
  const restoredSessionRef = useRef<boolean | null>(null);
  if (!loading && restoredSessionRef.current === null) {
    restoredSessionRef.current = !!user;
  }
  const restoredSession = restoredSessionRef.current === true;
  const showedSignInRef = useRef(false);
  const [phoneCheckedThisVisit, setPhoneCheckedThisVisit] = useState(false);

  // Logged-in visits to /signin (e.g. Create account while already signed in):
  // change the URL immediately; profile/terms still gate the UI.
  useEffect(() => {
    if (!restoredSession || !user) return;
    if (!route || route.name !== 'signin') return;
    const dest = continuePathFrom(route);
    const url = new URL(dest, window.location.origin);
    // Work URLs (`/`, `/routines`) are not portal routes. Rewriting
    // `route` to `pathToRoute('/')` is `null` and blanks the tree.
    if (!isPortalPath(url.pathname)) return;
    replacePath(`${url.pathname}${url.search}`);
    const next = pathToRoute(url.pathname, url.search);
    if (next) setRoute(next);
  }, [restoredSession, user, route]);

  // After SMS the Firebase user exists immediately. Stay in AuthExperience
  // until terms + verified email are on users/{uid}, then continue to `next`.
  useEffect(() => {
    if (loading || !user || !recoveryReady) return;
    if (!route || !needsOnboarding(route)) return;
    if (!termsAccepted) return;
    if (!emailVerified) {
      const dest = continuePathFrom(route);
      let destIsPortal = false;
      try {
        destIsPortal = isPortalPath(new URL(dest, window.location.origin).pathname);
      } catch {
        destIsPortal = false;
      }
      if (!destIsPortal) {
        goPath(dest);
        return;
      }
      if (route.name !== 'email') {
        const emailRoute: AppRoute = {
          name: 'email',
          nextPath: dest,
        };
        replacePath(pathForRoute(emailRoute));
        setRoute(emailRoute);
      }
      return;
    }
    if (route.name === 'signin' || route.name === 'email') {
      goPath(continuePathFrom(route));
    }
  }, [loading, user, recoveryReady, termsAccepted, emailVerified, route, goPath]);

  if (loading && !user) {
    return <PortalLoader />;
  }

  if (!route) {
    return null;
  }

  // Public plan selection lives on x9.app (marketing). Keep /pricing as a
  // stable redirect for bookmarks and legacy portal links.
  if (route.name === 'pricing') {
    return (
      <PricingRedirect />
    );
  }

  // The creation wizards answer their questions before anyone has an account,
  // so they must return above the gates below — and they run their own SMS and
  // terms steps inline, at the end, without changing the URL.
  if (route.name === 'startPasta' || route.name === 'startRoutine') {
    const Page = route.name === 'startPasta' ? StartPastaPage : StartRoutinePage;
    return (
      <Suspense fallback={<PortalLoader />}>
        <Page
          user={user}
          recoveryReady={recoveryReady}
          termsAccepted={termsAccepted}
          onDone={goPath}
        />
      </Suspense>
    );
  }

  const hasPhone = !!user && !user.isAnonymous && !!user.phoneNumber;
  const sessionAdmitted = isAdmittedPhoneSession(user, {
    ready: recoveryReady,
    termsAccepted,
  });
  const phoneProven = phoneCheckedThisVisit || sessionAdmitted;

  // Restored work sessions already have users/{uid}. Wait for that doc
  // before deciding to force SMS — otherwise Equipe / Conta flash Sign in.
  if (user && !recoveryReady && !phoneCheckedThisVisit) {
    return <PortalLoader />;
  }

  // Keep one AuthExperience mounted across SMS → terms → email so the
  // Three.js canvas and header do not remount.
  //
  // Order is fixed: prove the phone this visit, then terms if missing —
  // unless a phone + Terms session is already admitted (the work board).
  // A leftover Firebase session without Terms must not skip the number.
  const onAuthSurface =
    route.name === 'signin'
    || route.name === 'verify-email'
    || requiresAuth(route)
    || !!forcedRoute;
  const showSignIn = onAuthSurface && !phoneProven;
  const awaitingProfile = phoneProven && !!user && !recoveryReady;
  const showTermsGate =
    phoneProven
    && hasPhone
    && recoveryReady
    && !termsAccepted;
  const showEmailGate =
    phoneProven
    && hasPhone
    && recoveryReady
    && termsAccepted
    && !emailVerified
    && route.name !== 'verify-email'
    && continueDestIsPortal;

  if (showSignIn) showedSignInRef.current = true;
  const keepHeroCanvas = showedSignInRef.current;

  // Restored sessions never boot WebGL. New SMS keeps the canvas across gates.
  if (awaitingProfile && !keepHeroCanvas) {
    return <PortalLoader />;
  }

  if (showSignIn || awaitingProfile || showTermsGate || showEmailGate) {
    return (
      <Suspense fallback={<PortalLoader />}>
        <AuthExperience
          onBack={() => openMarketingHome()}
          enableHeroCanvas={keepHeroCanvas || !!forcedRoute}
        >
          {showSignIn ? (
            <SignInForm
              onSuccess={() => {
                setPhoneCheckedThisVisit(true);
              }}
              banner={
                handoffError ? (
                  <p className="text-sm text-amber-700">
                    Não foi possível entrar pela sessão do app ({handoffError}). Use o SMS abaixo.
                  </p>
                ) : undefined
              }
            />
          ) : showTermsGate && user ? (
            <TermsGateForm user={user} />
          ) : showEmailGate ? (
            <EmailGateForm
              recovery={recovery}
              onVerified={() => goPath(continuePathFrom(route))}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Preparando sua conta…</p>
          )}
        </AuthExperience>
      </Suspense>
    );
  }

  if (route.name === 'verify-email' && user) {
    return (
      <VerifyEmailPage
        token={route.token}
        onDone={() => goPath(PORTAL_PATHS.HOME)}
      />
    );
  }

  if (route.name === 'signin' || route.name === 'email') {
    return <PortalLoader />;
  }

  if (route.name === 'checkout') {
    if (!recovery.recoveryEmail) return null; // unreachable: gated above
    if (route.mode === 'pack-upgrade') {
      const sc = route.seatCount ?? 3;
      const vp = route.volumePacks ?? 0;
      return (
        <PackUpgradeCheckoutPage
          seatCount={sc}
          volumePacks={vp}
          onBack={() => go({ name: 'account' })}
          onDone={() => go({ name: 'account' })}
        />
      );
    }
    return (
      <CheckoutPage
        interval={route.interval}
        seatCount={route.seatCount}
        seatPacks={route.seatPacks}
        volumePacks={route.volumePacks}
        verifiedEmail={recovery.recoveryEmail}
        onBack={() => go({ name: 'account' })}
        onSwitchToYearly={() => go({
          name: 'checkout',
          interval: 'YEARLY',
          seatCount: route.seatCount,
          seatPacks: route.seatPacks,
          volumePacks: route.volumePacks,
        })}
        onPending={(subscriptionId) => go({ name: 'pending', subscriptionId })}
        onPackUpdated={() => go({ name: 'account' })}
      />
    );
  }

  if (route.name === 'pending') {
    return (
      <PendingPage
        subscriptionId={route.subscriptionId}
        onViewBilling={() => go({ name: 'account' })}
      />
    );
  }

  if (route.name === 'seats') {
    return <SeatsPage onNavigate={goPath} />;
  }

  if (route.name === 'usage' || route.name === 'account') {
    return (
      <AccountPage
        onNavigate={goPath}
        initialTab={route.name === 'usage' ? 'uso' : undefined}
        verifiedEmail={recovery.recoveryEmail}
        emailVerified={emailVerified}
        onAddEmail={() =>
          go({ name: 'email', nextPath: PORTAL_PATHS.ACCOUNT })
        }
        onSelectMonthly={() => go({ name: 'checkout', interval: 'MONTHLY' })}
        onSelectYearly={() => go({ name: 'checkout', interval: 'YEARLY' })}
        onCheckout={({ interval, seatCount, volumePacks, mode }) =>
          go({
            name: 'checkout',
            interval,
            seatCount,
            volumePacks,
            ...(mode === 'pack-upgrade' ? { mode: 'pack-upgrade' as const } : {}),
          })
        }
        onSignedOut={() => openMarketingPricing()}
      />
    );
  }

  return <PricingRedirect />;
}

function PricingRedirect() {
  useEffect(() => {
    window.location.replace(marketingPricingUrl());
  }, []);
  return <PortalLoader />;
}
